import { useEffect, useSyncExternalStore, type ReactNode } from 'react'
import type { AppConfig } from '../config/env'
import { queryClient } from '../query/client'
import { getSessionClient } from './sessionClient'
import { SessionContext } from './useSession'

export interface AuthProviderProps {
  readonly config: AppConfig
  readonly children: ReactNode
}
export function AuthProvider({ config, children }: AuthProviderProps) {
  const client = getSessionClient(config.apiBaseUrl)
  const state = useSyncExternalStore(client.subscribe, client.getSnapshot)
  useEffect(() => {
    let identity: string | null = null
    const unsubscribe = client.subscribe(() => {
      const user = client.getSnapshot().user
      const next = user ? `${user.issuer}\n${user.sub}` : null
      if (identity !== next) {
        void queryClient.cancelQueries()
        queryClient.clear()
        identity = next
      }
    })
    void client.restore()
    return unsubscribe
  }, [client])
  return (
    <SessionContext
      value={{
        ...state,
        signIn: (returnTo) => {
          void client.login(returnTo)
        },
        signOut: () => {
          void client.logout().then((result) => {
            if (!result) return
            if (result.revocation === 'failed') {
              window.alert('You are signed out of Argus. Remote revocation was not confirmed.')
            }
            window.location.assign(result.logout_url)
          })
        },
        refresh: async () => {
          await client.renew(true).catch(() => undefined)
        },
        retry: () => {
          void client.retry()
        },
      }}
    >
      {children}
    </SessionContext>
  )
}
